// library imports
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Button, Row, Stack, Card, Pagination, WordCloudView, List, Icon, TextInput, ProgressBar, Uploader, LoadingBox } from "_core/components/common/UI";
import { CustomDateRangePicker, DateRadio, FitList } from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";
import AdminCreateReportApi from "apis/AdminCreateReportApi";
import { InfoCard } from 'components/common/parts';
import Encoding from 'encoding-japanese';
import { ConvertMarkdownToHTML, GetEncodedCSV } from '_core/utils/common';

// styles imports
import './AdminUserChatReport.scss';

const AdminUserChatReport = () => {
    const dispatch = useDispatch();
    const ExecAdminCreateReportApi = AdminCreateReportApi();
    const { tUser } = useSelector((store) => store.transaction);
    const { initializeState } = useSelector((store) => store.scene);

    const [files, setFiles] = useState([]);
    const [chatLogs, setChatLogs] = useState([]);
    const [markdown, setMarkdown] = useState("");
    const [html, setHtml] = useState("");
    const [isShowResultModal, setIsShowResultModal] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        dispatch(setLoading(false));
    }, [dispatch]);

    useEffect(() => {
        const handleScroll = (e) => {
            // イベント発生元が document の場合は window.scrollY を、それ以外（特定のdivなど）ならその要素の scrollTop を使用
            const msgBody = e.target;
            // documentの場合は body や documentElement を見る必要があるが、今回のケースでは PageContent 内のスクロールを想定

            // スクロール可能な状態か判定 (コンテンツの高さ > 表示領域の高さ)
            const isScrollable = msgBody.scrollHeight > msgBody.clientHeight;

            if (isScrollable) {
                setShowScrollButton(true);
            } else {
                setShowScrollButton(false);
            }
        };

        // 第3引数 true (useCapture) を指定して、子要素のスクロールイベントもフックする
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, []);

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return
        }

        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete))
    }, [initializeState]);

    useEffect(() => {
        if (!files || files.length === 0) {
            setChatLogs([]);
            return;
        }

        const file = files[0].file;
        const reader = new FileReader();

        reader.onload = (e) => {
            const codes = new Uint8Array(e.target.result);
            const encoding = Encoding.detect(codes);
            const unicodeString = Encoding.convert(codes, {
                to: 'UNICODE',
                from: encoding
            });
            const text = Encoding.codeToString(unicodeString);

            const rows = parseCsvToRows(text);

            if (rows.length === 0) {
                setChatLogs([]);
                return;
            }

            // 1. ヘッダー行を取得
            const headerRow = rows[0];
            
            // 2. カラム位置を特定するためのキーワード定義
            // これらを headerRow から探します
            const questionKeywords = ["質問", "Question", "問い合わせ"];
            const answerKeywords = ["返答", "回答", "Answer", "返信"];

            // 3. カラムインデックスの探索
            let questionIdx = -1;
            let answerIdx = -1;

            headerRow.forEach((col, index) => {
                // クオート除去とトリムをして比較しやすくする
                const colName = col.replace(/^"|"$/g, '').trim(); 
                
                // 部分一致で判定（"質問内容"などもヒットするように）
                if (questionIdx === -1 && questionKeywords.some(k => colName.includes(k))) {
                    questionIdx = index;
                }
                if (answerIdx === -1 && answerKeywords.some(k => colName.includes(k))) {
                    answerIdx = index;
                }
            });

            // 見つからなかった場合のフォールバック（従来の固定位置：質問=2, 回答=3）
            // ※CSVの列が増減しても、キーワードが見つかれば正しい列を参照できます
            if (questionIdx === -1) questionIdx = 2;
            if (answerIdx === -1) answerIdx = 3;

            // ヘッダーを除外してデータ抽出
            const dataRows = rows.slice(1);
            const logs = dataRows.map(row => {
                // 行が必要なカラム数を持っているかチェック
                if (row.length <= Math.max(questionIdx, answerIdx)) return null;
                
                return {
                    question: row[questionIdx] ? row[questionIdx].replace(/^"|"$/g, '') : "",
                    answer: row[answerIdx] ? row[answerIdx].replace(/^"|"$/g, '') : ""
                };
            }).filter(item => item !== null && (item.question || item.answer));

            setChatLogs(logs);
        };
        reader.readAsArrayBuffer(file);

    }, [files]);

    const parseCsvToRows = (text) => {
        // 簡易CSVパーサー（ダブルクオート含むセル対応）
        const rows = [];
        // 改行コード正規化
        const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalizedText.split('\n').filter(l => l.trim() !== "");

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const cells = [];
            let cur = '';
            let inQuote = false;
            for (let j = 0; j < line.length; j++) {
                const ch = line[j];
                if (ch === '"') {
                    inQuote = !inQuote;
                    continue;
                }
                if (ch === ',' && !inQuote) {
                    cells.push(cur);
                    cur = '';
                } else {
                    cur += ch;
                }
            }
            cells.push(cur);
            rows.push(cells);
        }
        return rows;
    }

    const onGenerate = async () => {
        if (!files || files.length === 0) return;

        // 通信中の表示を出す
        // dispatch(setLoading(true));
        setIsGenerating(true);
        const result = await ExecAdminCreateReportApi(files[0].file, files[0].file.name);
        // 通信中の表示を消す
        // dispatch(setLoading(false));
        setIsGenerating(false);

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        const reportMarkdown = result.response.report;
        setMarkdown(reportMarkdown);
        const reportHTML = ConvertMarkdownToHTML(reportMarkdown);
        setHtml(reportHTML);
        setIsShowResultModal(true);
    }

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(markdown);
            setIsCopied(true);
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
            // 成功時のフィードバックが必要なら追加
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const topRef = useRef(null);

    const scrollToRef = (targetRef) => {
        console.log(targetRef.current);
        if (targetRef.current) {
            console.log("scroll started");
            targetRef.current.scrollIntoView({
                behavior: "smooth", // スムーズなスクロール
                block: "start",    // スクロール位置（'start', 'center', 'end' など）
            });
        }
    };

    return (
        <PageContent pageId="AdminUserChatReport">
            <div ref={topRef}></div>
            <PageHeader>
                <Stack className="spacing-4px">
                    <div className="page-header-title">レポート作成</div>
                    <div className="page-header-detail">チャットのログからレポートを作成します。</div>
                </Stack>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    <Stack>
                        <Card className="base-card margin-bottom-16px">
                            <Stack className="spacing-16px">
                                <Uploader
                                    className="width-full"
                                    accept={[".csv"]}
                                    onFileChanged={(files) => {
                                        setFiles(files)
                                    }}
                                    files={files}
                                    isSwap={true}
                                    minimum={false}
                                />
                                <Row className="flex-center">
                                    <Button className={"primary width-320px " + (files && files.length > 0 && !isGenerating ? "" : "disabled")} onClick={onGenerate} disabled={!files || files.length === 0 || isGenerating}>
                                        {isGenerating ?
                                            <Row className="flex-center spacing-8px">
                                                <LoadingBox />
                                                <div>作成中</div>
                                            </Row>
                                            : "レポート作成"}
                                    </Button>
                                </Row>
                            </Stack>
                        </Card>

                        {files && files.length > 0 && chatLogs.length > 0 &&
                            <Card className="base-card">
                                <div className="stack chat-area spacing-32px align-center width-full">
                                    {chatLogs.map((item, index) => {
                                        return (
                                            <React.Fragment key={index}>
                                                {/* Q (User) */}
                                                <Stack className="flex-left width-full chat-user spacing-8px">
                                                    <Stack className="flex-left width-full">
                                                        <Row className="flex-left width-full spacing-4px">
                                                            <Icon icon="person" size={20} className="chat-icon" />
                                                            <div className="align-right text-size-medium text-semibold">ユーザー</div>
                                                        </Row>
                                                    </Stack>
                                                    <Stack className="flex-right width-full chat-text">
                                                        <div dangerouslySetInnerHTML={{ __html: item.question }} />
                                                    </Stack>
                                                </Stack>
                                                {/* A (Assistant) */}
                                                <Stack className="flex-left width-full chat-assistant spacing-8px">
                                                    <Stack className="flex-left width-full">
                                                        <Row className="flex-left width-full spacing-4px">
                                                            <Icon icon="exclamation-circle" size={20} className="chat-icon" />
                                                            <div className="text-size-medium text-semibold">{Constants.AI_NAME}</div>
                                                        </Row>
                                                    </Stack>
                                                    <Row className="flex-left width-full chat-text">
                                                        <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                                                    </Row>
                                                </Stack>
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                            </Card>
                        }
                    </Stack>
                    {showScrollButton &&
                        <Button className="scroll-button primary" onClick={() => { scrollToRef(topRef) }}>
                            <Icon icon="chevron-up" size={28} className="copy-icon" />
                        </Button>
                    }
                </PageContainer>
            </PageWrapper>

            {/* 結果表示モーダル */}
            {isShowResultModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="text-size-h2">レポート結果</div>
                            {/* <Button className="close-button secondary-border" onClick={() => setIsShowResultModal(false)}>
                                <Icon icon="x-lg" size={24} />
                            </Button> */}
                        </div>
                        <div className="modal-body markdown-preview-card">
                            <div className="markdown-content" dangerouslySetInnerHTML={{ __html: html }} />
                        </div>
                        <Stack className="background-gray align-center width-full flex-center spacing-16px">
                            <InfoCard style={{background: "none"}}>
                                <div>この画面を閉じるとレポート結果は消去されます。必要な場合は内容をコピーしてください。</div>
                            </InfoCard>
                        </Stack>
                        <div className="modal-footer">
                            <Button className="secondary-border" onClick={copyToClipboard}>
                                <Row className="flex-center spacing-8px">
                                    <Icon icon={isCopied ? "check-lg" : "copy"} size={20} />
                                    <div>コピー</div>
                                </Row>
                            </Button>
                            <Button className="primary" onClick={() => setIsShowResultModal(false)}>
                                閉じる
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </PageContent>
    );
};

export default AdminUserChatReport;